// Shared helpers for ApplicationDetail and its per-tab components.
// Kept here to avoid circular imports between ApplicationDetail.jsx
// and the tab files that need these utilities.

import { parseCapabilityURN } from './parseCapabilityURN.js';

// safeStr returns v only when it is a non-empty string; callers use
// `safeStr(v) || fallback` so that object-valued fields (Go zero-value
// structs serialised without omitempty) don't reach React as children
// (React Error #31).
export function safeStr(v) {
  return typeof v === 'string' && v ? v : null;
}

// urnShort — lightweight last-segment extractor.
// Retained for the backward-compat re-export from ApplicationDetail.jsx;
// JOBE-0148.4 (ActTab) may retire it once that tab is redesigned.
// AccessTab (JOBE-0148.2) now uses parseCapabilityURN directly via accessViewToMatrix.
export function urnShort(urn) {
  if (!urn || typeof urn !== 'string') return '';
  const idx = urn.lastIndexOf(':');
  return idx === -1 ? urn : urn.slice(idx + 1);
}

// accessViewToMatrix folds the JOBE-0132 wire shape
// `{ application, principals: [{ name, displayName, grants: [{ capabilityID, decision, via }] }] }`
// into the matrix the AccessTab and ActTab renderers consume
// (`{ principals, urns, grants }`). Principal URNs are exposed as
// `handle` (the key used to look up grants).
//
// Each entry in `urns` carries `{ urn, domain, action, label }` — the
// domain/action/label tuple comes from parseCapabilityURN (JOBE-0148.2).
// Columns are ordered alphabetical by domain then alphabetical by action
// within each domain.
// Used by both AccessTab and ActTab.
export function accessViewToMatrix(access) {
  if (!access || !Array.isArray(access.principals)) {
    return { principals: [], urns: [], grants: {} };
  }
  const urnSet = new Map();
  for (const p of access.principals) {
    for (const g of p.grants || []) {
      if (!urnSet.has(g.capabilityID)) {
        const parsed = parseCapabilityURN(g.capabilityID);
        urnSet.set(g.capabilityID, {
          urn: g.capabilityID,
          domain: parsed.domain,
          action: parsed.action,
          label: parsed.label,
        });
      }
    }
  }

  // Sort columns: alphabetical by domain (nulls last), then by action.
  const urns = Array.from(urnSet.values()).sort((a, b) => {
    const da = a.domain || '￿';
    const db = b.domain || '￿';
    if (da !== db) return da.localeCompare(db);
    return (a.action || '').localeCompare(b.action || '');
  });

  const principals = access.principals.map((p) => ({
    handle: p.name,
    title: safeStr(p.displayName) || safeStr(p.name) || '',
    kind: p.name && p.name.startsWith('urn:jobe:agent:') ? 'agent' : 'principal',
  }));
  const grants = {};
  for (const p of access.principals) {
    for (const g of p.grants || []) {
      grants[`${p.name}|${g.capabilityID}`] = safeStr(g.decision) || 'none';
    }
  }
  return { principals, urns, grants };
}
